import { useEffect, useState } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { Link } from 'react-router-dom';
import { Mail, Pencil, Phone, Plus, Search, Trash2 } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import {
  useCreateMeetingContact,
  useDeleteMeetingContact,
  useMeetingContacts,
  useUpdateMeetingContact,
} from '../../hooks/useMeetingLog';
import { usePermissions } from '../../hooks/usePermissions';
import type { MeetingContact, MeetingContactInput } from '../../lib/meetingLogApi';
import { SkeletonTable } from '../ui/Skeleton';
import { useConfirm } from '../ui/ConfirmProvider';
import { ContactFormModal } from './ContactFormModal';

/** The directory of external people we have met (Meetings page, "Persoane"). */
export function ContactsTab() {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canManage = can('meetings', 'canManageContacts');
  const confirm = useConfirm();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<MeetingContact | null>(null);

  // Search as the user types (debounced); Enter still searches right away.
  const debouncedSearch = useDebouncedValue(searchInput.trim());
  useEffect(() => {
    if (debouncedSearch !== search) {
      setSearch(debouncedSearch);
      setPage(1);
    }
    // Only a settled keystroke triggers this.
  }, [debouncedSearch]);

  const { data, isLoading, isError } = useMeetingContacts({
    search: search || undefined,
    page,
    limit: 50,
  });
  const contacts = data?.data ?? [];
  const meta = data?.meta;

  const createMutation = useCreateMeetingContact();
  const updateMutation = useUpdateMeetingContact();
  const deleteMutation = useDeleteMeetingContact();

  async function handleDelete(c: MeetingContact) {
    if (await confirm({ message: t('meetings.contacts.confirmDelete'), danger: true })) {
      deleteMutation.mutate(c.id);
    }
  }

  async function handleCreate(input: MeetingContactInput) {
    await createMutation.mutateAsync(input);
    setShowCreate(false);
  }

  async function handleUpdate(input: MeetingContactInput) {
    if (!editing) return;
    await updateMutation.mutateAsync({ id: editing.id, patch: input });
    setEditing(null);
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
          className="flex-1 sm:max-w-md"
        >
          <label className="ui-field-label">{t('common.search')}</label>
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('meetings.contacts.search')}
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </form>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            <Plus size={16} /> {t('meetings.contacts.new')}
          </button>
        )}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <SkeletonTable rows={6} columns={5} />
        ) : isError ? (
          <p className="text-red-600">{t('meetings.contacts.error')}</p>
        ) : contacts.length === 0 ? (
          <p className="py-10 text-center text-gray-500">{t('meetings.contacts.empty')}</p>
        ) : (
          <>
            {/* Phones: one card per person, with the same actions. */}
            <ul className="space-y-2 md:hidden">
              {contacts.map((c) => (
                <li key={c.id} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900">{c.fullName}</p>
                      <p className="truncate text-xs text-gray-500">
                        {[c.role, c.companyName].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    {canManage && (
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing(c)}
                          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          aria-label={t('common.edit')}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(c)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          aria-label={t('common.delete')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                  {(c.email || c.phone) && (
                    <div className="mt-2 flex flex-col gap-1 text-sm text-gray-700">
                      {c.email && (
                        <a
                          href={`mailto:${c.email}`}
                          className="inline-flex min-w-0 items-center gap-1.5 hover:underline"
                        >
                          <Mail size={13} className="shrink-0 text-gray-400" />
                          <span className="truncate">{c.email}</span>
                        </a>
                      )}
                      {c.phone && (
                        <a
                          href={`tel:${c.phone}`}
                          className="inline-flex items-center gap-1.5 hover:underline"
                        >
                          <Phone size={13} className="shrink-0 text-gray-400" /> {c.phone}
                        </a>
                      )}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                    {c.client ? (
                      <Link
                        to={`/clients/${c.client.id}`}
                        className="truncate text-brand-700 hover:underline"
                      >
                        {c.client.name}
                      </Link>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                    {c.meetingsCount ? (
                      <Link
                        to={`/meetings?contactId=${c.id}`}
                        className="shrink-0 text-brand-700 hover:underline"
                      >
                        {t('meetings.contacts.colMeetings')}: {c.meetingsCount}
                      </Link>
                    ) : (
                      <span className="shrink-0 text-gray-400">
                        {t('meetings.contacts.colMeetings')}: 0
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto rounded-lg border border-gray-200 bg-white md:block">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2">{t('meetings.contacts.colName')}</th>
                    <th className="px-4 py-2">{t('meetings.contacts.colCompany')}</th>
                    <th className="px-4 py-2">{t('meetings.contacts.colClient')}</th>
                    <th className="px-4 py-2">{t('meetings.contacts.colContact')}</th>
                    <th className="px-4 py-2 text-right">{t('meetings.contacts.colMeetings')}</th>
                    {canManage && <th className="px-4 py-2" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {contacts.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">{c.fullName}</div>
                        {c.role && <div className="text-xs text-gray-500">{c.role}</div>}
                      </td>
                      <td className="px-4 py-2 text-gray-700">{c.companyName ?? '—'}</td>
                      <td className="px-4 py-2">
                        {c.client ? (
                          <Link
                            to={`/clients/${c.client.id}`}
                            className="text-brand-700 hover:underline"
                          >
                            {c.client.name}
                          </Link>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        <div className="flex flex-col gap-0.5">
                          {c.email && (
                            <a
                              href={`mailto:${c.email}`}
                              className="inline-flex items-center gap-1 hover:underline"
                            >
                              <Mail size={12} className="text-gray-400" /> {c.email}
                            </a>
                          )}
                          {c.phone && (
                            <a
                              href={`tel:${c.phone}`}
                              className="inline-flex items-center gap-1 hover:underline"
                            >
                              <Phone size={12} className="text-gray-400" /> {c.phone}
                            </a>
                          )}
                          {!c.email && !c.phone && <span className="text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-700">
                        {c.meetingsCount ? (
                          <Link
                            to={`/meetings?contactId=${c.id}`}
                            className="text-brand-700 hover:underline"
                          >
                            {c.meetingsCount}
                          </Link>
                        ) : (
                          0
                        )}
                      </td>
                      {canManage && (
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setEditing(c)}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                              aria-label={t('common.edit')}
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleDelete(c)}
                              className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                              aria-label={t('common.delete')}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {meta && meta.totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
            <span>{t('common.pageOf', { page: meta.page, total: meta.totalPages })}</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={meta.page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <button
                type="button"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <ContactFormModal
          submitting={createMutation.isPending}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}
      {editing && (
        <ContactFormModal
          initial={editing}
          submitting={updateMutation.isPending}
          onClose={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      )}
    </div>
  );
}
